<template>
  <div class="import-client">
    <div class="import-client__heading">
      <AkParagraph class="import-client__title">
        {{ $t('network.importContactsModal.title') }}
      </AkParagraph>

      <AkParagraph
        size="sm"
        class="ds-mb-4"
      >
        {{ $t('network.importContactsModal.advantages') }}
      </AkParagraph>
      <ul class="ds-list-disc ds-pl-7">
        <li class="ds-mb-2">
          <b class="ds-font-bold">{{ $t('network.importContactsModal.advantages.1') }}</b>
        </li>
        <li class="ds-mb-2">
          <b class="ds-font-bold">{{ $t('network.importContactsModal.advantages.2') }}</b>
          {{ $t('network.importContactsModal.advantages.2.highlight') }}
        </li>
        <li>{{ $t('network.importContactsModal.advantages.4') }}</li>
      </ul>
    </div>

    <div class="import-client__content">
      <form @submit.prevent="submit">
        <div class="form-single">
          <AkSelect
            v-model="form.language"
            :label="$t('Contacts language')"
            data-testid="selectLanguageContact"
            :options="languages"
            :placeholder="$t('Select the language')"
          />
        </div>
        <div class="form-single">
          <AkField :label="$t('Contacts details')">
            <AkTextarea
              v-model="form.emails"
              data-testid="textAreaEmails"
              :rows="5"
              :placeholder="$t('Copy / paste your contacts email addresses here')"
              :has-error="errors.emails"
              @input="errors.emails = false"
            />
          </AkField>
        </div>
        <div
          v-if="errors.emails"
          class="errors"
        >
          <span>{{ $t('At least one email address is not valid in this list.') }}</span>
        </div>
        <div
          v-if="errors.response"
          class="errors"
        >
          <span>{{ errors.response }}</span>
        </div>
        <div
          v-if="showSendInvitesCheckbox"
          class="form-single import-client__checkbox-container"
        >
          <AkCheckbox
            class="import-client__send-invites-checkbox"
            :checked="sendInvites"
            value="sendInvites"
            :text="$t('brand.network.importClients.sendInvites.checkbox')"
            data-testid="sendInvitesCheckbox"
            @change="sendInvites = $event"
          />
          <AkTooltipInformation
            class="import-client__send-invites-tooltip"
            :auto-hide="true"
            :content="$t('brand.network.importClients.sendInvites.tooltip')"
            placement="top"
            trigger="hover"
            type="invert"
          >
            <AkIcon
              class="import-client__send-invites-icon"
              size="sm"
              symbol="info-circle"
            />
          </AkTooltipInformation>
        </div>
      </form>
    </div>

    <div class="import-client__footer">
      <AkButton
        data-testid="importClientsStepButtonSave"
        :disabled="!isValid"
        size="lg"
        @click="submit"
      >
        {{ $t('Add your contacts') }}
      </AkButton>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { AkParagraph, AkSelect, AkTextarea, AkField, AkCheckbox, AkTooltipInformation, AkIcon } from '@ankorstore/design-system';
import { LanguageOption } from '@monolith/legacy/types/campaign';
import { FeatureFlag, isEnabled } from '@client/monolith/legacy/services/features';

export default defineComponent({
  name: 'ImportClientsStepInvitation',
  components: { AkParagraph, AkSelect, AkTextarea, AkField, AkCheckbox, AkTooltipInformation, AkIcon },
  props: {
    languages: {
      type: Array as PropType<LanguageOption[]>,
      required: true,
      default: null,
    },
  },
  emits: ['importClientForm'],
  data(): {
    form: {
      language: string;
      type: string;
      emails: string;
    };
    errors: {
      emails: boolean;
      response: string;
    };
    sendInvites: boolean;
  } {
    return {
      form: {
        language: '',
        type: 'historical',
        emails: '',
      },
      errors: {
        emails: false,
        response: null,
      },
      sendInvites: true,
    };
  },
  computed: {
    showSendInvitesCheckbox(): boolean {
      return isEnabled(FeatureFlag.AddContactsAutoInvite);
    },
    isValid(): boolean {
      return this.form.language !== '' && this.form.emails !== '' && !this.errors.emails;
    },
    areEmailsValids(): boolean {
      return this.getSplittedList.some((email) => email.match(/^\w(?:[+-_.]?\w)*@\w(?:[-_.]?\w)*\.(?:[a-z]{2,}$)/i));
    },
    getSplittedList(): string[] {
      return this.form.emails.split(/(?:\r\n|\r|\n|\s+|,|;)/g).filter((element) => element !== '');
    },
  },
  methods: {
    submit() {
      if (!this.areEmailsValids) {
        return (this.errors.emails = true);
      }
      return this.$emit('importClientForm', { ...this.form, sendInvites: this.sendInvites });
    },
  },
});
</script>
<style lang="scss" scoped>
@import '@css/vue-import';

.import-client {
  &__heading {
    @apply ds-p-5 ds-pb-0 ds-text-left;
  }

  &__title {
    @apply ds-text-xl ds-font-bold ds-my-4 ds-text-center;
  }

  &__text {
    @apply ds-text-base;
    &--no-margin {
      @apply ds-mb-0;
    }
    &--bottom-margin {
      @apply ds-mb-2;
    }
    &--bold {
      @apply ds-font-bold ds-text-lg;
    }
  }
  &__content {
    @apply ds-px-5 ds-pt-5 ds-text-left ds-overflow-y-auto;
  }
  &__footer {
    @apply ds-px-5 ds-pb-5;
    @apply ds-flex ds-items-center ds-justify-center ds-flex-row ds-gap-4;
  }
  form {
    @apply ds-mb-0;
    label:not(.ak-checkbox) {
      @apply ds-text-base;
    }
    textarea {
      @apply ds-w-full ds-block ds-border ds-border-solid ds-border-neutral-300;
      &::placeholder {
        @apply ds-text-neutral-700;
      }
    }
  }
  &__checkbox-container {
    @apply ds-flex ds-items-center ds-gap-1.5 ds-justify-start;
  }
  &__send-invites-checkbox.ak-checkbox {
    display: inline-block;
    width: max-content;
    flex: none;
  }
  &__send-invites-tooltip {
    @apply ds-shrink-0;
  }
  &__send-invites-icon {
    @apply ds-text-neutral-700 ds-relative ds-top-px;
  }
  .form-single {
    @apply ds-mb-6;
  }
}

.errors {
  @apply ds-text-error-700;
  margin-bottom: 15px;
  text-align: center;
  font-size: 12px;
  line-height: 12px;
}
</style>
